import cn from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import { loadJs } from '../../helpers/loadJs';

import Index from './Controls';
import type { IFeatureCollection, IItem } from './interface';
import Panel from './Panel';
import style from './style.module.scss';

const DEFAULT_ZOOM = 10;
const MOSCOW = [55.751574, 37.573856];

interface IMapProps {
  className?: string;
  zoom?: number;
  center?: number[];
  disableBehaviors?: string[];
  items: IItem[];
}

const Map: React.FC<IMapProps> = memo((props: IMapProps) => {
  const { className, items, center = MOSCOW, zoom = DEFAULT_ZOOM, disableBehaviors = [] } = props;

  const [selected, setSelected] = useState<IItem | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  let { current: collection } = useRef(null);
  const [currentCollectionUpd, setCurrentCollectionUpd] = useState(collection);

  const updateCollection = (newValue) => {
    collection = newValue;
    setCurrentCollectionUpd(newValue);
  };

  let { current: map } = useRef(null);
  const isMobile = useIsMobile();

  const setMapZoom = useCallback(
    (mapZoom: number): void => {
      map?.setZoom(mapZoom, { checkZoomRange: true, useMapMargin: true });
    },
    [map],
  );

  const handleClickZoomIn = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault();
      map && setMapZoom(map.getZoom() + 1);
    },
    [map, setMapZoom],
  );

  const handleClickZoomOut = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault();

      map && setMapZoom(map.getZoom() - 1);
    },
    [map, setMapZoom],
  );

  const handleClickHideInfoPanel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();

      setSelected((data) => {
        if (data) {
          collection?.objects.setObjectProperties(data.id, {
            className: '',
          });
        }

        return null;
      });
    },
    [collection],
  );

  const setMapCenter = useCallback(
    (mapCenter: number[], callback?: () => void, animation = false): void => {
      const options: {
        delay?: number;
        duration?: number;
        useMapMargin?: boolean;
      } = {};

      if (animation) {
        options.delay = 0;
        options.duration = 300;
      }

      options.useMapMargin = true;

      map?.setCenter(mapCenter, map.getZoom(), options).then(
        () => {
          if (callback) {
            callback();
          }
        },
        (err: any) => {
          console.log(err);
        },
      );
    },
    [map],
  );

  const handleSelectGeoObject = useCallback(
    (event: any): void => {
      const id = event.get('objectId');
      const object = collection?.objects.getById(id);
      const {
        geometry,
        properties: { data },
      } = object;

      collection?.objects.setObjectProperties(id, {
        className: 'selected',
      });

      setSelected((value) => {
        if (value) {
          collection?.objects.setObjectProperties(value.id, {
            className: '',
          });
        }

        return null;
      });

      setMapCenter(
        geometry.coordinates,
        () => {
          setSelected(data);
        },
        true,
      );
    },
    [collection, setMapCenter],
  );

  const addToMap = useCallback(
    (itemsCollection: any): void => {
      map.geoObjects.add(itemsCollection);
    },
    [map],
  );

  const buildFeatureCollection = useCallback(
    (itemsCollection: IItem[]): IFeatureCollection => ({
      type: 'FeatureCollection',
      features: itemsCollection.map((item) => ({
        type: 'Feature',
        id: item.id,
        geometry: {
          type: 'Point',
          coordinates: [item.latitude, item.longitude],
        },
        properties: {
          hintContent: item?.name,
          data: item,
          className: '',
        },
      })),
    }),
    [],
  );

  const addGeoObjects = useCallback(
    (itemsCollection: IItem[]): void => {
      collection?.removeAll();
      itemsCollection && collection?.add(buildFeatureCollection(itemsCollection));
    },
    [buildFeatureCollection, collection],
  );

  useEffect(() => {
    map && setMapZoom(zoom);
  }, [map, setMapZoom, zoom]);

  useEffect(() => {
    map && setMapCenter(center);
  }, [center, map, setMapCenter]);

  useEffect(() => {
    if (map) {
      if (isMobile) {
        map.margin.destroy();
        map.margin.addArea({
          bottom: 0,
          left: 0,
          width: '100%',
          height: '58%',
        });
      } else {
        map.margin.destroy();
        map.margin.addArea({
          top: 0,
          right: 0,
          width: 300,
          height: '100%',
        });
      }
    }
  }, [isMobile, map]);

  useEffect(() => {
    items && addGeoObjects(items);
  }, [addGeoObjects, items, currentCollectionUpd]);

  useEffect(() => {
    const onLoad = () =>
      (window as any).ymaps.ready(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        map = new window.ymaps.Map(
          container.current,
          {
            center,
            zoom,
            controls: [],
          },
          {
            minZoom: 5,
            maxZoom: 18,
            autoFitToViewport: true,
            hintFitPane: true,
            suppressMapOpenBlock: true,
          },
        );

        map.container.fitToViewport();
        map.behaviors.disable(['rightMouseButtonMagnifier', 'scrollZoom', ...disableBehaviors]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const collection = new window.ymaps.ObjectManager({
          clusterize: true,
          clusterHasBalloon: false,
          geoObjectOpenBalloonOnClick: false,
          gridSize: 128,
          viewportMargin: 64,
          maxZoom: 16,
        });

        collection.clusters.options.set({
          hasBalloon: false,
          clusterIconLayout: window.ymaps.templateLayoutFactory.createClass(
            '<div class="c-map-cluster">$[properties.geoObjects.length]</div>',
          ),
          clusterIconShape: {
            type: 'Circle',
            coordinates: [0, 0],
            radius: 14,
          },
        });

        collection.objects.options.set({
          hasBalloon: false,
          iconLayout: window.ymaps.templateLayoutFactory.createClass(
            '<div class="c-map-point $[properties.className]"/>',
          ),
          iconShape: {
            type: 'Rectangle',
            coordinates: [
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              [-20, 0],
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              [20, 20],
            ],
          },
        });

        collection.objects.events.add('click', handleSelectGeoObject);

        updateCollection(collection);
        addToMap(collection);
        items && addGeoObjects(items);

        map.events.add('click', (event: React.MouseEvent<HTMLButtonElement>) => {
          handleClickHideInfoPanel(event);
        });
      });

    (window as any).ymaps ? onLoad() : loadJs('//api-maps.yandex.ru/2.1/?lang=ru_RU', onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={cn(style.wrapper, className)}>
        <div ref={container} className={cn(style.MapContainer)}>
          <div>
            <div>
              <Index onClickZoomIn={handleClickZoomIn} onClickZoomOut={handleClickZoomOut} />
              {!isMobile ? <Panel branch={selected} onClose={handleClickHideInfoPanel} /> : null}
            </div>
          </div>
        </div>
      </div>

      {isMobile ? <Panel branch={selected} onClose={handleClickHideInfoPanel} /> : null}
    </>
  );
});

export default Map;
